import React, { useState, useEffect } from "react";
import {
    getAnnouncementBanners,
    createAnnouncementBanner,
    updateAnnouncementBanner,
    deleteAnnouncementBanner,
    toggleAnnouncementBannerStatus
} from "../../../../firebase/announcement-functions";
import { useLanguage } from "../../../../context/LanguageContext";

const AnnouncementBannersTab = ({ storeId }) => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [activeLanguageTab, setActiveLanguageTab] = useState('en');
    const [togglingBannerId, setTogglingBannerId] = useState(null);
    const { language } = useLanguage();

    // Translation object for admin UI
    const translations = {
        en: {
            title: "Announcement Banners",
            createNew: "Create New Banner",
            cancel: "Cancel",
            editBanner: "Edit Announcement Banner",
            createBanner: "Create New Banner",
            bannerText: "Banner Text",
            displayOrder: "Display Order",
            orderNote: "Lower numbers display first",
            active: "Active",
            saving: "Saving...",
            updateBanner: "Update Banner",
            createButton: "Create Banner",
            existingBanners: "Existing Banners",
            nobanners: "No announcement banners created yet.",
            createFirst: "Create Your First Banner",
            order: "Order",
            status: "Status",
            active: "Active",
            inactive: "Inactive",
            edit: "Edit",
            activate: "Activate",
            deactivate: "Deactivate",
            delete: "Delete",
            moveUp: "Move up",
            moveDown: "Move down",
            loading: "Loading announcement banners...",
            english: "English",
            arabic: "Arabic"
        },
        ar: {
            title: "لافتات الإعلان",
            createNew: "إنشاء لافتة جديدة",
            cancel: "إلغاء",
            editBanner: "تحرير لافتة الإعلان",
            createBanner: "إنشاء لافتة جديدة",
            bannerText: "نص اللافتة",
            displayOrder: "ترتيب العرض",
            orderNote: "الأرقام الأقل تظهر أولاً",
            active: "نشط",
            saving: "جاري الحفظ...",
            updateBanner: "تحديث اللافتة",
            createButton: "إنشاء لافتة",
            existingBanners: "اللافتات الموجودة",
            nobanners: "لم يتم إنشاء أي لافتات إعلان بعد.",
            createFirst: "إنشاء أول لافتة لك",
            order: "الترتيب",
            status: "الحالة",
            active: "نشط",
            inactive: "غير نشط",
            edit: "تحرير",
            activate: "تفعيل",
            deactivate: "إلغاء تفعيل",
            delete: "حذف",
            moveUp: "تحريك لأعلى",
            moveDown: "تحريك لأسفل",
            loading: "جاري تحميل لافتات الإعلان...",
            english: "الإنجليزية",
            arabic: "العربية"
        }
    };

    const t = translations[language] || translations.en;

    // Form state for creating/editing announcement banners
    const [formData, setFormData] = useState({
        text: "",
        textAr: "",
        isActive: true,
        displayOrder: 0,
        linkUrl: "",


        multilingual: {
            en: {
                text: "",

            },
            ar: {
                text: "",

            }
        }
    });

    // Load announcement banners on component mount
    useEffect(() => {
        if (storeId) {
            loadBanners();
        }
    }, [storeId]);

    // Clear messages after delay
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess("");
                setError("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    const loadBanners = async () => {
        try {
            setLoading(true);
            const bannerList = await getAnnouncementBanners(storeId);
            setBanners(bannerList);
        } catch (err) {
            console.error("Error loading announcement banners:", err);
            setError("Failed to load announcement banners");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            text: "",
            textAr: "",
            isActive: true,
            displayOrder: 0,
            linkUrl: "",


            multilingual: {
                en: {
                    text: "",

                },
                ar: {
                    text: "",

                }
            }
        });
        setEditingBanner(null);
        setShowCreateForm(false);
        setActiveLanguageTab('en');
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox") {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else if (name.startsWith('multilingual.')) {
            // Handle multilingual fields
            const [, lang, field] = name.split('.');
            setFormData(prev => ({
                ...prev,
                multilingual: {
                    ...prev.multilingual,
                    [lang]: {
                        ...prev.multilingual[lang],
                        [field]: value
                    }
                },
                // Also update the main field for backward compatibility
                [field]: lang === 'en' ? value : prev[field],
                [`${field}${lang === 'ar' ? 'Ar' : ''}`]: value
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCreating(true);
        setError("");
        setSuccess("");

        try {
            // Prepare data for submission with multilingual support
            const submitData = {
                text: formData.multilingual.en.text || formData.text || '',
                isActive: formData.isActive,
                displayOrder: parseInt(formData.displayOrder) || 0,
                linkUrl: formData.linkUrl || '',

                multilingual: {
                    en: {
                        text: formData.multilingual.en.text || formData.text || '',

                    },
                    ar: {
                        text: formData.multilingual.ar.text || formData.textAr || '',

                    }
                }
            };

            if (editingBanner) {
                // Update existing banner
                await updateAnnouncementBanner(storeId, editingBanner.id, submitData);
                setSuccess("Announcement banner updated successfully");
            } else {
                // Create new banner
                await createAnnouncementBanner(storeId, submitData);
                setSuccess("Announcement banner created successfully");
            }

            resetForm();
            await loadBanners();
        } catch (err) {
            console.error("Error saving announcement banner:", err);
            setError(err.message || "Failed to save announcement banner");
        } finally {
            setCreating(false);
        }
    };

    const handleEdit = (banner) => {
        const multilingual = banner.multilingual || {
            en: { text: banner.text || '' },
            ar: { text: banner.textAr || '' }
        };

        setFormData({
            text: banner.text || multilingual.en.text || '',
            textAr: multilingual.ar.text || '',
            isActive: banner.isActive,
            displayOrder: banner.displayOrder.toString(),
            linkUrl: banner.linkUrl || "",


            multilingual: multilingual
        });
        setEditingBanner(banner);
        setShowCreateForm(true);
    };

    const handleDelete = async (bannerId) => {
        if (!window.confirm("Are you sure you want to delete this announcement banner?")) {
            return;
        }

        try {
            await deleteAnnouncementBanner(storeId, bannerId);
            setSuccess("Announcement banner deleted successfully");
            await loadBanners();
        } catch (err) {
            console.error("Error deleting announcement banner:", err);
            setError("Failed to delete announcement banner");
        }
    };

    const handleToggleStatus = async (banner) => {
        setTogglingBannerId(banner.id);
        try {
            await toggleAnnouncementBannerStatus(storeId, banner.id, !banner.isActive);
            setSuccess(`Banner ${!banner.isActive ? 'activated' : 'deactivated'} successfully`);
            await loadBanners();
        } catch (err) {
            console.error("Error toggling banner status:", err);
            setError("Failed to update banner status");
        } finally {
            setTogglingBannerId(null);
        }
    };

    const moveOrder = async (banner, direction) => {
        const newOrder = direction === 'up' ? banner.displayOrder - 1 : banner.displayOrder + 1;
        try {
            await updateAnnouncementBanner(storeId, banner.id, { displayOrder: newOrder });
            await loadBanners();
        } catch (err) {
            console.error("Error updating banner order:", err);
            setError("Failed to update banner order");
        }
    };

    if (loading) {
        return (
            <div className="announcement-banners-loading">
                <div className="loader"></div>
                <p>{t.loading}</p>
            </div>
        );
    }

    return (
        <div className="announcement-banners-tab">
            <div className="banners-header">
                <h3>{t.title}</h3>
                <button
                    type="button"
                    className="admin-primary-btn"
                    onClick={() => setShowCreateForm(!showCreateForm)}
                >
                    {showCreateForm ? t.cancel : t.createNew}
                </button>
            </div>

            {error && <div className="admin-error-message">{error}</div>}
            {success && <div className="admin-success-message">{success}</div>}

            {/* Create/Edit Form */}
            {showCreateForm && (
                <div className="banner-form-container">
                    <h4>{editingBanner ? t.editBanner : t.createBanner}</h4>

                    {/* Language Tabs */}
                    <div className="language-tabs">
                        <button
                            type="button"
                            className={`language-tab ${activeLanguageTab === 'en' ? 'active' : ''}`}
                            onClick={() => setActiveLanguageTab('en')}
                        >
                            {t.english}
                        </button>
                        <button
                            type="button"
                            className={`language-tab ${activeLanguageTab === 'ar' ? 'active' : ''}`}
                            onClick={() => setActiveLanguageTab('ar')}
                        >
                            {t.arabic}
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="banner-form">
                        {/* English Content */}
                        {activeLanguageTab === 'en' && (
                            <div className="language-content">
                                <h5>{t.english} {t.bannerText}</h5>
                                <div className="form-row">
                                    <div className="form-group full-width">
                                        <label htmlFor="text-en">{t.bannerText} *</label>
                                        <textarea
                                            id="text-en"
                                            name="multilingual.en.text"
                                            value={formData.multilingual.en.text}
                                            onChange={handleInputChange}
                                            placeholder="e.g., Free delivery on orders above AED 250!"
                                            required
                                            rows="3"
                                        />
                                    </div>
                                </div>


                            </div>
                        )}

                        {/* Arabic Content */}
                        {activeLanguageTab === 'ar' && (
                            <div className="language-content">
                                <h5>{t.arabic} {t.bannerText}</h5>
                                <div className="form-row">
                                    <div className="form-group full-width">
                                        <label htmlFor="text-ar">{t.bannerText}</label>
                                        <textarea
                                            id="text-ar"
                                            name="multilingual.ar.text"
                                            value={formData.multilingual.ar.text}
                                            onChange={handleInputChange}
                                            placeholder="مثال: توصيل مجاني للطلبات فوق 250 درهم!"
                                            rows="3"
                                            dir="rtl"
                                        />
                                    </div>
                                </div>

                                <div className="form-row">

                                </div>
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="displayOrder">{t.displayOrder}</label>
                                <input
                                    type="number"
                                    id="displayOrder"
                                    name="displayOrder"
                                    value={formData.displayOrder}
                                    onChange={handleInputChange}
                                    min="0"
                                    placeholder="0"
                                />
                                <small>{t.orderNote}</small>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <div className="checkbox-group">
                                    <label htmlFor="isActive" className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            id="isActive"
                                            name="isActive"
                                            checked={formData.isActive}
                                            onChange={handleInputChange}
                                        />
                                        {t.active}
                                    </label>
                                </div>
                            </div>
                        </div>



                        <div className="form-actions">
                            <button
                                type="button"
                                className="admin-secondary-btn"
                                onClick={resetForm}
                            >
                                {t.cancel}
                            </button>
                            <button
                                type="submit"
                                className="admin-primary-btn"
                                disabled={creating}
                            >
                                {creating ? t.saving : (editingBanner ? t.updateBanner : t.createButton)}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Banners List */}
            <div className="banners-list">
                <h4>{t.existingBanners} ({banners.length})</h4>

                {banners.length === 0 ? (
                    <div className="no-banners">
                        <p>{t.nobanners}</p>
                        <button
                            type="button"
                            className="admin-primary-btn"
                            onClick={() => setShowCreateForm(true)}
                        >
                            {t.createFirst}
                        </button>
                    </div>
                ) : (
                    <div className="banners-table">
                        {banners
                            .sort((a, b) => a.displayOrder - b.displayOrder)
                            .map((banner) => (
                                <div key={banner.id} className={`banner-card ${!banner.isActive ? "inactive" : ""}`}>
                                    <div className="banner-info">
                                        <div className="admi-banner-text">
                                            <strong>
                                                {language === 'ar' && banner.multilingual?.ar?.text
                                                    ? banner.multilingual.ar.text.substring(0, 100)
                                                    : banner.text.substring(0, 100)
                                                }
                                                {(language === 'ar' && banner.multilingual?.ar?.text
                                                    ? banner.multilingual.ar.text.length
                                                    : banner.text.length) > 100 ? '...' : ''}
                                            </strong>
                                        </div>
                                        <div className="banner-meta">
                                            <span>{t.order}: {banner.displayOrder}</span>
                                            <span className={`status-badge ${banner.isActive ? "active" : "inactive"}`}>
                                                {banner.isActive ? t.active : t.inactive}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="banner-actions">
                                        <button
                                            type="button"
                                            className="admin-secondary-btn small"
                                            onClick={() => moveOrder(banner, 'up')}
                                            disabled={banner.displayOrder === 0}
                                            title={t.moveUp}
                                        >
                                            ↑
                                        </button>
                                        <button
                                            type="button"
                                            className="admin-secondary-btn small"
                                            onClick={() => moveOrder(banner, 'down')}
                                            title={t.moveDown}
                                        >
                                            ↓
                                        </button>
                                        <div className="action-buttons">
                                            <button
                                                type="button"
                                                className="admin-secondary-btn small"
                                                onClick={() => handleEdit(banner)}
                                                title={t.edit}
                                            >
                                                {t.edit}
                                            </button>

                                            <button
                                                type="button"
                                                className="admin-secondary-btn small"
                                                onClick={() => handleToggleStatus(banner)}
                                                title={banner.isActive ? t.deactivate : t.activate}
                                                disabled={togglingBannerId === banner.id}
                                            >
                                                {togglingBannerId === banner.id ? t.saving : (banner.isActive ? t.deactivate : t.activate)}
                                            </button>

                                            <button
                                                type="button"
                                                className="admin-secondary-btn small"
                                                onClick={() => handleDelete(banner.id)}
                                                title={t.delete}
                                            >
                                                {t.delete}
                                            </button>
                                        </div>

                                    </div>
                                </div>
                            ))}
                    </div>
                )}
            </div>


        </div>
    );
};

export default AnnouncementBannersTab;